import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Calendar,
  Copy,
  Edit3,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Layout,
  Monitor,
  Palette,
  Plus,
  RotateCcw,
  Save,
  Settings,
  Smartphone,
  Trash2,
  Type,
  Upload,
  User,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { api } from '../../services/api';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const HomepageManagement = () => {
  const [activeTab, setActiveTab] = useState('banners');
  const [banners, setBanners] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');

  // Banner form state
  const [bannerForm, setBannerForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    backgroundImage: '',
    backgroundType: 'image', // 'image', 'gradient', 'video'
    gradientColors: ['#ff6b35', '#f7931e'],
    textColor: '#ffffff',
    position: 'center',
    isActive: true,
    order: 0,
    animation: 'fade',
    displayDuration: 5000
  });

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Video upload state
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  useEffect(() => {
    loadHomepageData();
  }, []);

  const loadHomepageData = async () => {
    setLoading(true);
    try {
      const [bannersRes, servicesRes, statsRes] = await Promise.all([
        api.get('/cms/banners/homepage'),
        api.get('/cms/services/homepage'),
        api.get('/cms/stats/homepage')
      ]);

      const bannersData = Array.isArray(bannersRes.data) ? bannersRes.data : (bannersRes.data?.data || []);
      const servicesData = Array.isArray(servicesRes.data) ? servicesRes.data : (servicesRes.data?.data || []);
      const statsData = Array.isArray(statsRes.data) ? statsRes.data : (statsRes.data?.data || []);

      // Ensure we always set arrays, and filter out any undefined/null elements
      setBanners(Array.isArray(bannersData) ? bannersData.filter(b => b != null) : []);
      setServices(Array.isArray(servicesData) ? servicesData.filter(s => s != null) : []);
      setStats(Array.isArray(statsData) ? statsData.filter(s => s != null) : []);
    } catch (error) {
      console.error('Failed to load homepage data:', error);
      toast.error('Could not load homepage data.');
      // Load mock data for development if API fails
      setBanners([
        {
          _id: '1',
          title: 'Professional Property Maintenance',
          subtitle: 'SwiftFixPro',
          description: 'Your trusted partner for all property maintenance needs',
          buttonText: 'Get Started',
          buttonLink: '/register',
          backgroundType: 'gradient',
          gradientColors: ['#ff6b35', '#f7931e'],
          isActive: true,
          order: 0
        }
      ]);
      setServices([
        {
          _id: '1',
          name: 'Plumbing Services',
          description: 'Professional plumbing repairs and installations',
          price: 'SGD 80',
          features: ['24/7 Emergency Service', 'Licensed Technicians', 'Warranty Included'],
          isActive: true
        }
      ]);
      setStats([
        { _id: '1', number: '500+', label: 'Happy Customers', isActive: true },
        { _id: '2', number: '50+', label: 'Expert Technicians', isActive: true },
        { _id: '3', number: '24/7', label: 'Support Available', isActive: true },
        { _id: '4', number: '4.9', label: 'Average Rating', isActive: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      // Use uploadFiles method which properly handles FormData
      const response = await api.uploadFiles('/cms/upload/banner-image', formData);

      if (response.success) {
        return response.data.imageUrl;
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setSelectedImageFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
      setBannerForm(prev => ({ ...prev, backgroundImage: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = async (file) => {
    if (!file) return null;

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);

      // Use uploadFiles method which properly handles FormData
      const response = await api.uploadFiles('/cms/upload/banner-video', formData);

      if (response.success) {
        return response.data.videoUrl;
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error('Failed to upload video');
      return null;
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video size must be less than 50MB');
      return;
    }

    setSelectedVideoFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      setVideoPreview(event.target.result);
      setBannerForm(prev => ({ ...prev, videoUrl: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveBanner = async () => {
    try {
      let imageUrl = bannerForm.backgroundImage;
      let videoUrl = bannerForm.videoUrl;

      // Upload image if background type is image and a file was selected
      if (selectedImageFile && bannerForm.backgroundType === 'image') {
        const uploadedUrl = await handleImageUpload(selectedImageFile);
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        } else {
          toast.error('Failed to upload image. Please try again.');
          return;
        }
      }

      // Upload video if background type is video and a file was selected
      if (selectedVideoFile && bannerForm.backgroundType === 'video') {
        const uploadedUrl = await handleVideoUpload(selectedVideoFile);
        if (uploadedUrl) {
          videoUrl = uploadedUrl;
        } else {
          toast.error('Failed to upload video. Please try again.');
          return;
        }
      }

      const bannerData = {
        ...bannerForm,
        backgroundImage: bannerForm.backgroundType === 'image' ? imageUrl : '',
        videoUrl: bannerForm.backgroundType === 'video' ? videoUrl : '',
        location: 'homepage'
      };

      if (editingBanner) {
        const response = await api.put(`/cms/banners/homepage/${editingBanner._id}`, bannerData);
        setBanners(prev => prev.map(banner =>
          banner._id === editingBanner._id ? response.data.data : banner
        ));
        toast.success('Banner updated successfully!');
      } else {
        const response = await api.post('/cms/banners/homepage', bannerData);
        setBanners(prev => [...prev, response.data.data]);
        toast.success('Banner created successfully!');
      }

      setShowBannerModal(false);
      setEditingBanner(null);
      resetBannerForm();
    } catch (error) {
      console.error('Failed to save banner:', error);
      toast.error('Failed to save banner');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await api.delete(`/cms/banners/homepage/${bannerId}`);
      setBanners(prev => prev.filter(banner => banner._id !== bannerId));
      toast.success('Banner deleted successfully!');
    } catch (error) {
      console.error('Failed to delete banner:', error);
      toast.error(`Failed to delete banner: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleToggleBannerStatus = async (bannerId, currentStatus) => {
    try {
      await api.patch(`/cms/banners/homepage/${bannerId}/toggle`, {
        isActive: !currentStatus
      });
      setBanners(prev => prev.map(banner =>
        banner._id === bannerId ? { ...banner, isActive: !currentStatus } : banner
      ));
      toast.success(`Banner ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Failed to toggle banner status:', error);
      toast.error('Failed to update banner status');
    }
  };

  const handleReorderBanner = async (bannerId, direction) => {
    const currentIndex = banners.findIndex(b => b._id === bannerId);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === banners.length - 1)
    ) return;

    try {
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      const newBanners = [...banners];
      [newBanners[currentIndex], newBanners[newIndex]] = [newBanners[newIndex], newBanners[currentIndex]];
      newBanners.forEach((banner, index) => {
        banner.order = index;
      });

      setBanners(newBanners);

      await api.patch(`/cms/banners/homepage/${bannerId}/reorder`, {
        newOrder: newBanners[newIndex].order
      });

      toast.success('Banner order updated!');
    } catch (error) {
      console.error('Failed to reorder banner:', error);
      toast.error('Failed to reorder banner');
    }
  };

  const resetImageUpload = () => {
    setSelectedImageFile(null);
    setImagePreview(null);
    setUploadingImage(false);
  };

  const resetBannerForm = () => {
    setBannerForm({
      title: '',
      subtitle: '',
      description: '',
      buttonText: '',
      buttonLink: '',
      backgroundImage: '',
      backgroundType: 'image',
      gradientColors: ['#ff6b35', '#f7931e'],
      textColor: '#ffffff',
      position: 'center',
      isActive: true,
      order: 0,
      animation: 'fade',
      displayDuration: 5000
    });
    resetImageUpload();
  };

  const openBannerModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        description: banner.description || '',
        buttonText: banner.buttonText || '',
        buttonLink: banner.buttonLink || '',
        backgroundImage: banner.backgroundImage || '',
        backgroundType: banner.backgroundType || 'image',
        gradientColors: banner.gradientColors || ['#ff6b35', '#f7931e'],
        textColor: banner.textColor || '#ffffff',
        position: banner.position || 'center',
        isActive: banner.isActive !== false,
        order: banner.order || 0,
        animation: banner.animation || 'fade',
        displayDuration: banner.displayDuration || 5000
      });
      if (banner.backgroundImage) {
        setImagePreview(banner.backgroundImage);
      }
    } else {
      setEditingBanner(null);
      resetBannerForm();
    }
    setShowBannerModal(true);
  };

  const tabs = [
    { id: 'banners', name: 'Banners', icon: ImageIcon, count: Array.isArray(banners) ? banners.length : 0 },
    { id: 'services', name: 'Services', icon: Settings, count: Array.isArray(services) ? services.length : 0 },
    { id: 'stats', name: 'Statistics', icon: BarChart3, count: Array.isArray(stats) ? stats.length : 0 },
    { id: 'layout', name: 'Layout', icon: Layout, count: 0 },
    { id: 'preview', name: 'Preview', icon: Monitor, count: 0 }
  ];

  return (
    <div className="min-h-screen bg-gray-50 -mt-8">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-600 text-white py-20 pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-3xl font-bold text-white">Homepage Management</h1>
              <p className="text-orange-100 mt-2">
                Create stunning homepage content that converts visitors into customers
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{Array.isArray(banners) ? banners.filter(b => b && b.isActive).length : 0}</div>
                <div className="text-sm text-orange-100">Active Banners</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{Array.isArray(services) ? services.filter(s => s && s.isActive).length : 0}</div>
                <div className="text-sm text-orange-100">Featured Services</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{Array.isArray(stats) ? stats.filter(s => s && s.isActive).length : 0}</div>
                <div className="text-sm text-orange-100">Statistics</div>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-2">
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={`p-2 rounded-lg transition-colors ${previewMode === 'desktop'
                      ? 'bg-white/20 text-white'
                      : 'text-orange-200 hover:text-white'
                    }`}
                  title="Desktop Preview"
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={`p-2 rounded-lg transition-colors ${previewMode === 'mobile'
                      ? 'bg-white/20 text-white'
                      : 'text-orange-200 hover:text-white'
                    }`}
                  title="Mobile Preview"
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-2">
            <p className="text-sm text-gray-600 font-medium">📋 Content Management</p>
          </div>
          <div className="border-b border-gray-200">
            <nav className="flex space-x-2 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center py-4 px-4 font-medium text-sm transition-all duration-200 rounded-t-lg relative ${isActive
                        ? 'text-orange-600 bg-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <Icon className={`w-5 h-5 mr-2 ${isActive ? 'text-orange-500' : ''}`} />
                    {tab.name}
                    {tab.count > 0 && (
                      <span className={`ml-2 py-1 px-2 rounded-full text-xs font-semibold ${isActive
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                        {tab.count}
                      </span>
                    )}
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-orange-500"
                      />
                    )}
                  </motion.button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-xl shadow-sm border">
          {loading ? (
            <div className="p-12 flex flex-col justify-center items-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-600 border-t-transparent absolute top-0"></div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🔄 Loading Homepage Data</h3>
                <p className="text-gray-600">Fetching banners, services, and statistics...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Banners Tab Content */}
              {activeTab === 'banners' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Homepage Banners</h2>
                      <p className="text-gray-600">Create and manage homepage hero banners</p>
                    </div>
                    <button
                      onClick={() => openBannerModal()}
                      className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Banner
                    </button>
                  </div>

                  <div className="space-y-6">
                    {Array.isArray(banners) && banners.length > 0 ? banners.filter(banner => banner && banner._id).map((banner, index) => (
                      <motion.div
                        key={banner._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="group bg-gradient-to-r from-white to-gray-50 border-2 border-gray-100 rounded-2xl p-6 hover:shadow-xl hover:border-orange-200 transition-all duration-300"
                      >
                        <div className="flex items-center space-x-6">
                          <div className="relative">
                            <div className="w-32 h-24 rounded-xl overflow-hidden bg-gray-100 shadow-lg">
                              {banner.backgroundImage ? (
                                <img
                                  src={banner.backgroundImage}
                                  alt={banner.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div
                                  className="w-full h-full flex items-center justify-center"
                                  style={{
                                    background: `linear-gradient(135deg, ${banner.gradientColors?.[0] || '#ff6b35'}, ${banner.gradientColors?.[1] || '#f7931e'})`
                                  }}
                                >
                                  <ImageIcon className="w-8 h-8 text-white opacity-60" />
                                </div>
                              )}
                            </div>
                            {banner.isActive && (
                              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                                LIVE
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <h3 className="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                                {banner.title || 'Untitled Banner'}
                              </h3>
                              <div className="flex items-center space-x-2">
                                {!banner.isActive && (
                                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full font-semibold">
                                    📴 Inactive
                                  </span>
                                )}
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                                  #{banner.order || index + 1}
                                </span>
                              </div>
                            </div>

                            {banner.subtitle && (
                              <p className="text-orange-600 font-medium mb-2">{banner.subtitle}</p>
                            )}

                            <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                              {banner.description || 'No description available'}
                            </p>

                            {banner.buttonText && (
                              <div className="flex items-center mt-2">
                                <span className="text-xs text-gray-500 mr-2">CTA:</span>
                                <span className="bg-gradient-to-r from-orange-400 to-orange-600 text-white px-3 py-1 rounded-lg text-xs font-semibold">
                                  {banner.buttonText}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col space-y-2">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleReorderBanner(banner._id, 'up')}
                                disabled={index === 0}
                                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Move up"
                              >
                                <ArrowUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReorderBanner(banner._id, 'down')}
                                disabled={index === banners.length - 1}
                                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Move down"
                              >
                                <ArrowDown className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleToggleBannerStatus(banner._id, banner.isActive)}
                                className={`p-2 rounded-lg transition-colors ${banner.isActive
                                    ? 'bg-green-100 hover:bg-green-200 text-green-700'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
                                  }`}
                                title={banner.isActive ? 'Deactivate' : 'Activate'}
                              >
                                {banner.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => openBannerModal(banner)}
                                className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                                title="Edit banner"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBanner(banner._id)}
                                className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                                title="Delete banner"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-16 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border-2 border-dashed border-orange-200"
                      >
                        <div className="bg-gradient-to-br from-orange-100 to-red-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                          <ImageIcon className="w-12 h-12 text-orange-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">🎨 No banners yet</h3>
                        <p className="text-gray-600 mb-8 max-w-md mx-auto">
                          Transform your homepage with eye-catching banners that convert visitors into customers
                        </p>
                        <button
                          onClick={() => openBannerModal()}
                          className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-200 transform hover:scale-105 font-semibold shadow-lg"
                        >
                          ✨ Create Your First Banner
                        </button>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}

              {/* Placeholder for other tabs */}
              {activeTab !== 'banners' && (
                <div className="p-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border-2 border-dashed border-blue-200"
                  >
                    <div className="bg-gradient-to-br from-blue-100 to-purple-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                      {(() => {
                        const currentTab = tabs.find(tab => tab.id === activeTab);
                        const Icon = currentTab?.icon || Settings;
                        return <Icon className="w-12 h-12 text-blue-500" />;
                      })()}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      🚧 {tabs.find(tab => tab.id === activeTab)?.name} Management
                    </h3>
                    <p className="text-gray-600 mb-8 max-w-md mx-auto">
                      This powerful feature is currently under development.
                      Get ready for an amazing content management experience!
                    </p>
                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={() => setActiveTab('banners')}
                        className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-200 font-semibold"
                      >
                        🏠 Manage Banners
                      </button>
                      <button
                        onClick={() => window.open('/', '_blank')}
                        className="bg-gray-600 text-white px-6 py-3 rounded-xl hover:bg-gray-700 transition-colors font-semibold"
                      >
                        👀 View Homepage
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Banner Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-4xl w-full max-h-screen overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingBanner ? 'Edit Banner' : 'Create New Banner'}
                </h3>
                <button
                  onClick={() => setShowBannerModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Content Section */}
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                <h4 className="font-semibold text-orange-900 mb-2 flex items-center">
                  <Type className="w-4 h-4 mr-2" />
                  Content Settings
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Banner Title *</label>
                  <input
                    type="text"
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter banner title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={bannerForm.subtitle}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Enter banner subtitle"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={bannerForm.description}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter banner description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                  <input
                    type="text"
                    value={bannerForm.buttonText}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, buttonText: e.target.value }))}
                    placeholder="e.g., Get Started"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Button Link</label>
                  <input
                    type="text"
                    value={bannerForm.buttonLink}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, buttonLink: e.target.value }))}
                    placeholder="e.g., /services or https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Text Style Section */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                  <Palette className="w-4 h-4 mr-2" />
                  Text Style & Positioning
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={bannerForm.textColor}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, textColor: e.target.value }))}
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={bannerForm.textColor}
                      onChange={(e) => setBannerForm(prev => ({ ...prev, textColor: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Text Position</label>
                  <select
                    value={bannerForm.position}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, position: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    value={bannerForm.order}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower number = higher priority</p>
                </div>
              </div>

              {/* Background Settings */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Background Settings
                </h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Type</label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setBannerForm(prev => ({ ...prev, backgroundType: 'image' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${bannerForm.backgroundType === 'image' ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <ImageIcon className="w-6 h-6 mx-auto mb-2" />
                    <span className="block font-medium">Image</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBannerForm(prev => ({ ...prev, backgroundType: 'gradient' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${bannerForm.backgroundType === 'gradient' ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <Palette className="w-6 h-6 mx-auto mb-2" />
                    <span className="block font-medium">Gradient</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBannerForm(prev => ({ ...prev, backgroundType: 'video' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${bannerForm.backgroundType === 'video' ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <Monitor className="w-6 h-6 mx-auto mb-2" />
                    <span className="block font-medium">Video</span>
                  </button>
                </div>
              </div>
              {bannerForm.backgroundType === 'image' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Image</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Banner preview" className="w-full h-32 object-cover rounded-lg mb-4" />
                        <div className="flex justify-center space-x-2">
                          <label className="cursor-pointer bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors">
                            <Upload className="w-4 h-4 mr-2 inline" />Change Image
                            <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" disabled={uploadingImage} />
                          </label>
                          <button type="button" onClick={() => { setImagePreview(null); setSelectedImageFile(null); setBannerForm(prev => ({ ...prev, backgroundImage: '' })); }} className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors">Remove</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">{uploadingImage ? 'Uploading image...' : 'Click to upload banner image'}</p>
                        <label className="cursor-pointer bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors inline-flex items-center">
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingImage ? 'Uploading...' : 'Select Image'}
                          <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" disabled={uploadingImage} />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">Supports: JPG, PNG, GIF (Max: 5MB)</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {bannerForm.backgroundType === 'gradient' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gradient Start Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={bannerForm.gradientColors[0]}
                        onChange={(e) => setBannerForm(prev => ({ ...prev, gradientColors: [e.target.value, prev.gradientColors[1]] }))}
                        className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={bannerForm.gradientColors[0]}
                        onChange={(e) => setBannerForm(prev => ({ ...prev, gradientColors: [e.target.value, prev.gradientColors[1]] }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                        placeholder="#ff6b35"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gradient End Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={bannerForm.gradientColors[1]}
                        onChange={(e) => setBannerForm(prev => ({ ...prev, gradientColors: [prev.gradientColors[0], e.target.value] }))}
                        className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={bannerForm.gradientColors[1]}
                        onChange={(e) => setBannerForm(prev => ({ ...prev, gradientColors: [prev.gradientColors[0], e.target.value] }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                        placeholder="#f7931e"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gradient Preview</label>
                    <div
                      className="h-16 rounded-lg"
                      style={{
                        background: `linear-gradient(135deg, ${bannerForm.gradientColors[0]}, ${bannerForm.gradientColors[1]})`
                      }}
                    ></div>
                  </div>
                </div>
              )}

              {bannerForm.backgroundType === 'video' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Video</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    {videoPreview ? (
                      <div className="relative">
                        <video
                          src={videoPreview}
                          className="w-full h-40 object-cover rounded-lg mb-4"
                          muted
                          loop
                          autoPlay
                        />
                        <div className="flex justify-center space-x-2">
                          <label className="cursor-pointer bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors">
                            <Upload className="w-4 h-4 mr-2 inline" />
                            Change Video
                            <input
                              type="file"
                              accept="video/mp4,video/webm"
                              onChange={handleVideoFileSelect}
                              className="hidden"
                              disabled={uploadingVideo}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setVideoPreview(null);
                              setSelectedVideoFile(null);
                              setBannerForm(prev => ({ ...prev, videoUrl: '' }));
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">
                          {uploadingVideo ? 'Uploading video...' : 'Click to upload background video'}
                        </p>
                        <label className="cursor-pointer bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors inline-flex items-center">
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingVideo ? 'Uploading...' : 'Select Video'}
                          <input
                            type="file"
                            accept="video/mp4,video/webm"
                            onChange={handleVideoFileSelect}
                            className="hidden"
                            disabled={uploadingVideo}
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">
                          Supports: MP4, WebM (Max: 50MB, Recommended: 1920x1080)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Animation & Timing Section */}
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Animation & Timing
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Animation Style</label>
                  <select
                    value={bannerForm.animation}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, animation: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="fade">Fade</option>
                    <option value="slide">Slide</option>
                    <option value="zoom">Zoom</option>
                    <option value="none">No Animation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Duration (seconds)</label>
                  <input
                    type="number"
                    value={bannerForm.displayDuration / 1000}
                    onChange={(e) => setBannerForm(prev => ({ ...prev, displayDuration: parseInt(e.target.value) * 1000 || 5000 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    min="3"
                    max="30"
                  />
                  <p className="text-xs text-gray-500 mt-1">How long banner shows in carousel (3-30s)</p>
                </div>
              </div>

              {/* Status Section */}
              <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Settings className="w-4 h-4 mr-2" />
                  Banner Status
                </h4>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={bannerForm.isActive}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Active (Show banner on homepage)
                </label>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Preview</label>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                  <div
                    className="h-64 flex items-center justify-center relative p-4"
                    style={{
                      background: bannerForm.backgroundType === 'image'
                        ? (imagePreview || bannerForm.backgroundImage
                          ? `url(${imagePreview || bannerForm.backgroundImage}) center/cover`
                          : '#f3f4f6')
                        : bannerForm.backgroundType === 'video'
                        ? '#1f2937'
                        : `linear-gradient(135deg, ${bannerForm.gradientColors[0]}, ${bannerForm.gradientColors[1]})`
                    }}
                  >
                    {bannerForm.backgroundType === 'image' && !imagePreview && !bannerForm.backgroundImage && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <ImageIcon className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    {bannerForm.backgroundType === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                        <Monitor className="w-16 h-16 text-gray-600" />
                        <span className="absolute bottom-4 text-gray-400 text-sm">Video: {bannerForm.videoUrl || 'No URL set'}</span>
                      </div>
                    )}
                    <div
                      className={`text-center z-10 relative ${
                        bannerForm.position === 'left' ? 'mr-auto' :
                        bannerForm.position === 'right' ? 'ml-auto' : ''
                      }`}
                      style={{ color: bannerForm.textColor }}
                    >
                      {bannerForm.subtitle && (
                        <span className="inline-block px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm mb-2 backdrop-blur-sm">
                          {bannerForm.subtitle}
                        </span>
                      )}
                      <h2 className="text-2xl font-bold mb-2">{bannerForm.title || 'Banner Title'}</h2>
                      {bannerForm.description && (<p className="opacity-90 mb-4 max-w-md text-sm">{bannerForm.description}</p>)}
                      {bannerForm.buttonText && (
                        <button className="bg-white px-6 py-2 rounded-lg font-medium shadow-lg" style={{ color: bannerForm.gradientColors?.[0] || '#ff6b35' }}>
                          {bannerForm.buttonText}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-2 text-xs text-gray-600 flex justify-between">
                    <span>Position: <strong>{bannerForm.position}</strong></span>
                    <span>Animation: <strong>{bannerForm.animation}</strong></span>
                    <span>Duration: <strong>{bannerForm.displayDuration / 1000}s</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button onClick={() => setShowBannerModal(false)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSaveBanner} disabled={uploadingImage} className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
                <Save className="w-4 h-4 mr-2" />
                {uploadingImage ? 'Uploading...' : editingBanner ? 'Update Banner' : 'Create Banner'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default HomepageManagement;